import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';

const ActivityLog = ({ activities = [], maxHeight = 180 }) => {
    const logRef = useRef(null);

    // Auto-scroll to bottom when new activity arrives
    useEffect(() => {
        if (logRef.current) {
            logRef.current.scrollTop = logRef.current.scrollHeight;
        }
    }, [activities]);

    const getEventIcon = (type) => {
        switch (type) {
            case 'success': return '✓';
            case 'upload': return '↑';
            case 'download': return '↓';
            case 'warning': return '!';
            case 'error': return '✗';
            default: return '●';
        }
    };

    const getEventColor = (type) => {
        switch (type) {
            case 'success': return '#4ade80';
            case 'upload': return '#5fd4c4';
            case 'download': return '#5fd4c4';
            case 'warning': return '#f59e0b';
            case 'error': return '#ef4444';
            default: return '#c8c8c8';
        }
    };


    return (
        <Box sx={{
            width: '100%',
            height: { xs: '100px', sm: '120px', md: maxHeight },
            background: '#0a1210',
            border: '1px solid #5fd4c4',
            borderRadius: '4px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: vortxTypography.mono,
        }}>
            {/* Header */}
            <Box sx={{
                borderBottom: '1px solid #5fd4c440',
                px: 2,
                py: 1,
                background: 'rgba(95, 212, 196, 0.05)',
            }}>
                <Typography sx={{
                    color: '#5fd4c4',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                }}>
                    ACTIVITY LOG
                </Typography>
            </Box>

            {/* Log entries */}
            <Box
                ref={logRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 1,
                    // Custom scrollbar
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(95, 212, 196, 0.1)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(95, 212, 196, 0.3)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(95, 212, 196, 0.5)',
                    },
                }}
            >
                {activities.length === 0 ? (
                    <Typography sx={{
                        color: '#666',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        fontStyle: 'italic',
                    }}>
                        [--:--:--] &gt; System ready. Awaiting commands...
                    </Typography>
                ) : (
                    activities.slice(-50).map((activity, index) => {
                        const age = Date.now() - activity.timestamp;
                        const isRecent = age < 30000; // 30 seconds
                        const opacity = isRecent ? 1 : 0.6;

                        return (
                            <Box
                                key={activity.id || index}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    py: 0.25,
                                    opacity,
                                    transition: 'opacity 1s ease',
                                }}
                            >
                                <Typography sx={{
                                    color: '#666',
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    minWidth: { xs: '50px', sm: '60px' },
                                }}>
                                    {activity.time}
                                </Typography>
                                <Typography sx={{
                                    color: getEventColor(activity.type),
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    minWidth: '12px',
                                }}>
                                    {getEventIcon(activity.type)}
                                </Typography>
                                <Typography sx={{
                                    color: '#5fd4c4',
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    minWidth: { xs: '25px', sm: '35px' },
                                }}>
                                    {activity.app}
                                </Typography>
                                <Typography sx={{
                                    color: '#666',
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                }}>
                                    &gt;
                                </Typography>
                                <Typography sx={{
                                    color: '#c8c8c8',
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    flex: 1,
                                }}>
                                    {activity.message}
                                </Typography>
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};

export default ActivityLog;
